import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import BrandLogo from './BrandLogo';
import { ctaPrimary } from '../../shared-theme/brand';

const StyledToolbar = styled(Toolbar)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
  backdropFilter: 'blur(24px)',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  padding: '12px 0',
}));

const NavButton = styled(Button)(() => ({
  color: '#3E301E',
  fontWeight: 500,
  fontSize: '0.925rem',
  textTransform: 'none',
  letterSpacing: 0,
  padding: '8px 14px',
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#B5683C',
  },
}));

const NAVBAR_HEIGHT = 64; // Approximate navbar height in pixels

export default function AppAppBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);

  const handleOpen = () => {
    setOpen(true);
    setIsClosing(false);
  };

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before removing from DOM
    setTimeout(() => {
      setOpen(false);
      setIsClosing(false);
    }, 200);
  };

  const toggleDrawer = () => {
    if (open) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  const handleBookingClick = () => {
    handleClose();
    const isMobile = window.innerWidth < 900;
    
    if (isMobile) {
      // On mobile, directly open the Calendly overlay
      window.dispatchEvent(new CustomEvent('openCalendly'));
    } else {
      // On desktop, scroll to section then open Calendly
      scrollToSection('booking');
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('openCalendly'));
      }, 500);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - NAVBAR_HEIGHT;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const scrollToTop = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <>
      {/* Mobile Menu Backdrop - rendered first, behind everything */}
      {open && (
        <Box
          onClick={handleClose}
          sx={{
            display: { xs: 'block', md: 'none' },
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            zIndex: 1099, // Just below AppBar's default 1100
            opacity: isClosing ? 0 : 1,
            transition: 'opacity 0.2s ease-out',
            animation: !isClosing ? 'fadeIn 0.2s ease-out' : 'none',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
          }}
        />
      )}
      
      <AppBar
        position="fixed"
        enableColorOnDark
        sx={{
          boxShadow: 0,
          bgcolor: 'rgba(253, 252, 249, 0.98)',
          backgroundImage: 'none',
          mt: 0,
          borderBottom: '1px solid',
          borderColor: '#E8E2D8',
        }}
      >
        {/* Toolbar with padding */}
        <Box sx={{ width: '100%', px: { xs: 2, sm: 4, md: 6 } }}>
          <StyledToolbar variant="dense" disableGutters>
            <Box
              onClick={scrollToTop}
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            >
              <BrandLogo />
            </Box>
            
            {/* Desktop Navigation - Center */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <NavButton onClick={() => scrollToSection('features')}>How it works</NavButton>
              <NavButton onClick={() => scrollToSection('platform')}>Platform</NavButton>
              <NavButton onClick={() => scrollToSection('testimonials')}>Customers</NavButton>
              <NavButton onClick={() => scrollToSection('faq')}>FAQ</NavButton>
              <NavButton onClick={() => window.location.href = 'mailto:info@openintake.ai'}>Contact</NavButton>
            </Box>

            {/* Desktop CTA */}
            <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleBookingClick}
                sx={{ ...ctaPrimary, px: 2.5, py: 0.75, fontSize: '0.9rem' }}
              >
                Book a demo
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton aria-label="Menu button" onClick={toggleDrawer}>
                {open ? <CloseRoundedIcon /> : <MenuIcon />}
              </IconButton>
            </Box>
          </StyledToolbar>
        </Box>

        {/* Mobile Dropdown Menu - Full width, outside the padded container */}
        {open && (
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              width: '100%',
              pb: 2,
              pt: 1,
              backgroundColor: 'rgba(253, 252, 249, 1)',
              opacity: isClosing ? 0 : 1,
              transform: isClosing ? 'translateY(-10px)' : 'translateY(0)',
              transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
              animation: !isClosing ? 'slideDown 0.2s ease-out' : 'none',
              '@keyframes slideDown': {
                '0%': { opacity: 0, transform: 'translateY(-10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' },
              },
            }}
          >
            <MenuItem
              onClick={() => { scrollToSection('features'); handleClose(); }}
              sx={{ py: 1.5, px: 3, color: '#3E301E', fontWeight: 500 }}
            >
              How it works
            </MenuItem>
            <MenuItem
              onClick={() => { scrollToSection('platform'); handleClose(); }}
              sx={{ py: 1.5, px: 3, color: '#3E301E', fontWeight: 500 }}
            >
              Platform
            </MenuItem>
            <MenuItem
              onClick={() => { scrollToSection('testimonials'); handleClose(); }}
              sx={{ py: 1.5, px: 3, color: '#3E301E', fontWeight: 500 }}
            >
              Customers
            </MenuItem>
            <MenuItem
              onClick={() => { scrollToSection('faq'); handleClose(); }}
              sx={{ py: 1.5, px: 3, color: '#3E301E', fontWeight: 500 }}
            >
              FAQ
            </MenuItem>
            <MenuItem
              onClick={() => { window.location.href = 'mailto:info@openintake.ai'; handleClose(); }}
              sx={{ py: 1.5, px: 3, color: '#3E301E', fontWeight: 500 }}
            >
              Contact
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ px: 3, pt: 1 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={handleBookingClick}
                sx={ctaPrimary}
              >
                Book a demo
              </Button>
            </Box>
          </Box>
        )}
      </AppBar>
    </>
  );
}
